import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Save,
  ArrowUp,
  ArrowDown,
  Zap,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import type {
  ZapierAutomation,
  AutomationField,
  AutomationFieldType,
  AutomationFieldSource,
  AutomationRole,
} from '@sca-uk/shared';
import { fetchZapierAutomations, saveZapierAutomations } from '@lib/automations';

const FIELD_TYPES: { value: AutomationFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text (multi-line)' },
  { value: 'date', label: 'Date' },
  { value: 'currency', label: 'Currency' },
  { value: 'number', label: 'Number' },
];

const FIELD_SOURCES: { value: AutomationFieldSource; label: string }[] = [
  { value: 'manual', label: 'Manual Input' },
  { value: 'beacon_name', label: 'Beacon Person Name' },
  { value: 'beacon_organisation', label: 'Beacon Organisation' },
  { value: 'auto_date', label: "Auto (Today's Date)" },
];

const AVAILABLE_ROLES: { value: AutomationRole; label: string }[] = [
  { value: 'Admin', label: 'Admin' },
  { value: 'Regular', label: 'Regular' },
];

function createEmptyField(): AutomationField {
  return {
    key: '',
    displayName: '',
    fieldType: 'text',
    source: 'manual',
    defaultValue: '',
    required: true,
  };
}

function createEmptyAutomation(): ZapierAutomation {
  const now = new Date().toISOString();
  return {
    id: '',
    name: '',
    description: '',
    webhookUrl: '',
    fields: [createEmptyField()],
    allowedRoles: ['Admin', 'Regular'],
    isActive: false,
    createdAt: now,
    updatedAt: now,
  };
}

export default function ZapierAutomationSettings() {
  const [automations, setAutomations] = useState<ZapierAutomation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Auto-hide feedback
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Load automations
  useEffect(() => {
    (async () => {
      try {
        const settings = await fetchZapierAutomations();
        setAutomations(settings.automations);
      } catch {
        setFeedback({ type: 'error', message: 'Failed to load Zapier automations' });
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    // Validate
    for (const auto of automations) {
      if (!auto.name.trim()) {
        setFeedback({ type: 'error', message: 'Each automation must have a name' });
        return;
      }
      if (!auto.webhookUrl.trim() && auto.isActive) {
        setFeedback({
          type: 'error',
          message: `"${auto.name}" is active but has no Webhook URL`,
        });
        return;
      }
      if (auto.allowedRoles.length === 0) {
        setFeedback({
          type: 'error',
          message: `"${auto.name}" must have at least one allowed role`,
        });
        return;
      }
      if (auto.fields.length === 0) {
        setFeedback({
          type: 'error',
          message: `"${auto.name}" must have at least one field`,
        });
        return;
      }
      for (const f of auto.fields) {
        if (!f.key.trim() || !f.displayName.trim()) {
          setFeedback({
            type: 'error',
            message: `All fields in "${auto.name}" must have a payload key and display name`,
          });
          return;
        }
      }
    }

    setIsSaving(true);
    try {
      const saved = await saveZapierAutomations(automations);
      setAutomations(saved.automations);
      setFeedback({ type: 'success', message: 'Zapier automations saved' });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const addAutomation = () => {
    const auto = createEmptyAutomation();
    auto.id = `automation-${Date.now()}`;
    setAutomations((prev) => [...prev, auto]);
    setExpandedId(auto.id);
  };

  const removeAutomation = (id: string) => {
    setAutomations((prev) => prev.filter((a) => a.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  const updateAutomation = (id: string, updates: Partial<ZapierAutomation>) => {
    setAutomations((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        const updated = { ...a, ...updates, updatedAt: new Date().toISOString() };
        // Auto-generate slug from name if id is a temp id
        if (updates.name && a.id.startsWith('automation-')) {
          updated.id = updates.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
        }
        return updated;
      }),
    );
  };

  // ── Field operations ─────────────────────────────────────────────────
  const addField = (automationId: string) => {
    setAutomations((prev) =>
      prev.map((a) =>
        a.id === automationId ? { ...a, fields: [...a.fields, createEmptyField()] } : a,
      ),
    );
  };

  const removeField = (automationId: string, fieldIndex: number) => {
    setAutomations((prev) =>
      prev.map((a) =>
        a.id === automationId
          ? { ...a, fields: a.fields.filter((_, i) => i !== fieldIndex) }
          : a,
      ),
    );
  };

  const updateField = (
    automationId: string,
    fieldIndex: number,
    updates: Partial<AutomationField>,
  ) => {
    setAutomations((prev) =>
      prev.map((a) =>
        a.id === automationId
          ? {
              ...a,
              fields: a.fields.map((f, i) => (i === fieldIndex ? { ...f, ...updates } : f)),
            }
          : a,
      ),
    );
  };

  const moveField = (automationId: string, fieldIndex: number, direction: 'up' | 'down') => {
    setAutomations((prev) =>
      prev.map((a) => {
        if (a.id !== automationId) return a;
        const fields = [...a.fields];
        const newIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
        if (newIndex < 0 || newIndex >= fields.length) return a;
        [fields[fieldIndex], fields[newIndex]] = [fields[newIndex]!, fields[fieldIndex]!];
        return { ...a, fields };
      }),
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading Zapier automations...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-sca-purple-600" />
          <h3 className="text-base font-semibold text-gray-900">Zapier Automations</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addAutomation}
            className="btn-secondary text-xs flex items-center gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Automation
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary text-xs flex items-center gap-1"
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Register Zapier Zaps as automations. Each automation defines a webhook URL, the fields to
        collect, and which roles can trigger it. Webhook URLs are provided by "Webhooks by Zapier"
        catch hooks.
      </p>

      {/* Feedback */}
      {feedback && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            feedback.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
          )}
          {feedback.message}
        </div>
      )}

      {automations.length === 0 ? (
        <p className="text-sm text-gray-500 py-4">
          No automations configured. Click "Add Automation" to create one.
        </p>
      ) : (
        <div className="space-y-3">
          {automations.map((auto) => (
            <AutomationCard
              key={auto.id}
              automation={auto}
              isExpanded={expandedId === auto.id}
              onToggle={() => setExpandedId(expandedId === auto.id ? null : auto.id)}
              onUpdate={(updates) => updateAutomation(auto.id, updates)}
              onRemove={() => removeAutomation(auto.id)}
              onAddField={() => addField(auto.id)}
              onRemoveField={(i) => removeField(auto.id, i)}
              onUpdateField={(i, u) => updateField(auto.id, i, u)}
              onMoveField={(i, d) => moveField(auto.id, i, d)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Automation Card ────────────────────────────────────────────────────────────

function AutomationCard({
  automation,
  isExpanded,
  onToggle,
  onUpdate,
  onRemove,
  onAddField,
  onRemoveField,
  onUpdateField,
  onMoveField,
}: {
  automation: ZapierAutomation;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (updates: Partial<ZapierAutomation>) => void;
  onRemove: () => void;
  onAddField: () => void;
  onRemoveField: (index: number) => void;
  onUpdateField: (index: number, updates: Partial<AutomationField>) => void;
  onMoveField: (index: number, direction: 'up' | 'down') => void;
}) {
  const toggleRole = (role: AutomationRole) => {
    const current = automation.allowedRoles;
    const next = current.includes(role)
      ? current.filter((r) => r !== role)
      : [...current, role];
    onUpdate({ allowedRoles: next as AutomationRole[] });
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
          <span className="font-medium text-sm text-gray-900">
            {automation.name || 'Untitled Automation'}
          </span>
          <span className="text-xs text-gray-400">{automation.fields.length} fields</span>
          {automation.isActive ? (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Active
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Inactive
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
          title="Remove automation"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Expanded body */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-4 border-t">
          {/* Automation details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Automation Name</label>
              <input
                type="text"
                className="input"
                value={automation.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                placeholder="e.g. Fundraising Thank You Certificate"
              />
            </div>
            <div>
              <label className="label">Webhook URL</label>
              <input
                type="url"
                className="input"
                value={automation.webhookUrl}
                onChange={(e) => onUpdate({ webhookUrl: e.target.value })}
                placeholder="https://hooks.zapier.com/hooks/catch/..."
              />
            </div>
          </div>

          <div>
            <label className="label">Description (optional)</label>
            <input
              type="text"
              className="input"
              value={automation.description ?? ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder="Brief description of what this automation does"
            />
          </div>

          {/* Role permissions */}
          <div>
            <label className="label">Allowed Roles</label>
            <div className="flex items-center gap-4">
              {AVAILABLE_ROLES.map((roleOption) => (
                <label
                  key={roleOption.value}
                  className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={automation.allowedRoles.includes(roleOption.value)}
                    onChange={() => toggleRole(roleOption.value)}
                    className="rounded border-gray-300 text-sca-purple-600 focus:ring-sca-purple-500"
                  />
                  {roleOption.label}
                </label>
              ))}
              <span className="text-xs text-gray-400">(ReadOnly can never trigger)</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`active-${automation.id}`}
              checked={automation.isActive}
              onChange={(e) => onUpdate({ isActive: e.target.checked })}
              className="rounded border-gray-300 text-sca-purple-600 focus:ring-sca-purple-500"
            />
            <label htmlFor={`active-${automation.id}`} className="text-sm text-gray-700">
              Active (available for triggering)
            </label>
          </div>

          {/* Fields table */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Payload Fields</h4>
              <button
                onClick={onAddField}
                className="text-xs text-sca-purple-600 hover:text-sca-purple-700 flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Add Field
              </button>
            </div>

            <div className="space-y-2">
              {automation.fields.map((field, index) => (
                <FieldRow
                  key={index}
                  field={field}
                  index={index}
                  total={automation.fields.length}
                  onUpdate={(updates) => onUpdateField(index, updates)}
                  onRemove={() => onRemoveField(index)}
                  onMove={(dir) => onMoveField(index, dir)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Field Row ────────────────────────────────────────────────────────────────

function FieldRow({
  field,
  index,
  total,
  onUpdate,
  onRemove,
  onMove,
}: {
  field: AutomationField;
  index: number;
  total: number;
  onUpdate: (updates: Partial<AutomationField>) => void;
  onRemove: () => void;
  onMove: (direction: 'up' | 'down') => void;
}) {
  return (
    <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-md">
      {/* Reorder buttons */}
      <div className="flex flex-col gap-0.5 pt-1">
        <button
          onClick={() => onMove('up')}
          disabled={index === 0}
          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          title="Move up"
        >
          <ArrowUp className="h-3 w-3" />
        </button>
        <button
          onClick={() => onMove('down')}
          disabled={index === total - 1}
          className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
          title="Move down"
        >
          <ArrowDown className="h-3 w-3" />
        </button>
      </div>

      {/* Field inputs */}
      <div className="flex-1 grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs text-gray-500">Payload Key</label>
          <input
            type="text"
            className="input text-xs"
            value={field.key}
            onChange={(e) => onUpdate({ key: e.target.value })}
            placeholder="e.g. name"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Display Name</label>
          <input
            type="text"
            className="input text-xs"
            value={field.displayName}
            onChange={(e) => onUpdate({ displayName: e.target.value })}
            placeholder="e.g. Recipient Name"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Data Type</label>
          <select
            className="input text-xs"
            value={field.fieldType}
            onChange={(e) => onUpdate({ fieldType: e.target.value as AutomationFieldType })}
          >
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Source</label>
          <select
            className="input text-xs"
            value={field.source}
            onChange={(e) => onUpdate({ source: e.target.value as AutomationFieldSource })}
          >
            {FIELD_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Default Value</label>
          <input
            type="text"
            className="input text-xs"
            value={field.defaultValue ?? ''}
            onChange={(e) => onUpdate({ defaultValue: e.target.value })}
            placeholder="Optional"
          />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate({ required: e.target.checked })}
              className="rounded border-gray-300 text-sca-purple-600 focus:ring-sca-purple-500"
            />
            Required
          </label>
        </div>
      </div>

      {/* Remove */}
      <button
        onClick={onRemove}
        className="p-1 mt-1 text-gray-400 hover:text-red-500"
        title="Remove field"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
